import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { SnackBarService } from '@shared/services/snack-bar.service';
import { environment } from 'src/environments/environment';
import { Treaty, TreatyType } from '../models/treaty.model';
import { catchError, EMPTY, map, Observable, throwError } from 'rxjs';
import { GENERAL_ERROR_MESSAGE } from '@shared/models/messages';
import { TreatyWithStats } from '../models/treaty-with-stats.model';
import { TreatyCasualty } from '../models/treaty-casualty.model';
import { TreatyProperty } from '../models/treaty-property.model';
import { parseErrorMessageFromBlob } from '@shared/utils/blob-error-message-parse.utils';

@Injectable({
  providedIn: 'root',
})
export class TreatyApiService {
  readonly http = inject(HttpClient);
  private readonly _messageService = inject(SnackBarService);
  readonly url = `${environment.apiUrl}/treaty`;

  downloadTreatyTemplate$(type: TreatyType): Observable<HttpResponse<Blob>> {
    return this.http.get(`${this.url}/template`, { responseType: 'blob', observe: 'response', params: { treatyType: type } }).pipe(
      catchError(error => {
        parseErrorMessageFromBlob(error).then(errorMessage =>
          this._messageService.showError(`Failed to retrieve template: ${errorMessage}.`)
        );
        return EMPTY;
      })
    );
  }

  createTreaty$(file: File, type: string, treatyPortfolioName?: string): Observable<Treaty> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('treatyType', type);
    if (treatyPortfolioName) {
      formData.append('treatyPortfolioName', treatyPortfolioName);
    }

    return this.http.post<Treaty>(`${this.url}`, formData).pipe(
      catchError(error => {
        return throwError(() => error);
      })
    );
  }

  getAllTreaties$(my: boolean) {
    const params = my ? { my: 'true' } : undefined;
    return this.http.get<Treaty[]>(`${this.url}`, { params }).pipe(
      map(res => res.map(item => ({ ...item, createdByName: item.createdBy?.legalFullName, type: this.getTreatyType(item) }))),
      catchError(error => {
        this._messageService.showError(`Failed to retrieve Bordereau Assessments: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  private getTreatyType(item: Treaty): string {
    if (item.isProperty) {
      return 'Occupancies';
    } else if (item.isCasualty) {
      return 'Insured Names';
    }
    return '';
  }

  getTreatyById$(treatyId: number): Observable<Treaty> {
    return this.http.get<Treaty>(`${this.url}/${treatyId}`).pipe(
      catchError(error => {
        this._messageService.showError(`Failed to retrieve Bordereau Assessment: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  getCasualtyWitStatsById$(treatyId: number): Observable<TreatyWithStats<TreatyCasualty>> {
    return this.http.get<TreatyWithStats<TreatyCasualty>>(`${this.url}/${treatyId}/casualties-with-stats`).pipe(
      catchError(error => {
        this._messageService.showError(`Failed to retrieve casualty with stats: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  exportCasualtyWithStatsById$(treatyId: number): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`${this.url}/${treatyId}/export-casualties-with-stats`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        catchError(error => {
          parseErrorMessageFromBlob(error).then(errorMessage =>
            this._messageService.showError(`Failed to export casualty with stat: ${errorMessage}.`)
          );
          return EMPTY;
        })
      );
  }

  getPropertyWithStatsById$(treatyId: number): Observable<TreatyWithStats<TreatyProperty>> {
    return this.http.get<TreatyWithStats<TreatyProperty>>(`${this.url}/${treatyId}/properties-with-stats`).pipe(
      catchError(error => {
        this._messageService.showError(`Failed to retrieve property with stats: ${error.error.message ?? GENERAL_ERROR_MESSAGE}.`);
        return EMPTY;
      })
    );
  }

  exportPropertyWithStatsById$(treatyId: number): Observable<HttpResponse<Blob>> {
    return this.http
      .get(`${this.url}/${treatyId}/export-properties-with-stats`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        catchError(error => {
          parseErrorMessageFromBlob(error).then(errorMessage =>
            this._messageService.showError(`Failed to export property with stat: ${errorMessage}.`)
          );
          return EMPTY;
        })
      );
  }
}
